import { getUnprocessableErrors } from '@10xlabs/utils'
import { ApolloError } from '@apollo/client'
import { Formik, FormikHelpers, useFormikContext } from 'formik'
import { ReactElement, useState } from 'react'
import { defineMessages, FormattedMessage, useIntl } from 'react-intl'
import { NavLink } from 'react-router-dom'
import styled from 'styled-components'

import { Input } from 'components/Input'
import { Button } from 'components/v2/Button'
import { Column } from 'components/v2/Column'
import { ErrorMessage } from 'components/v2/ErrorMessage'
import FormLayout from 'components/v2/FormLayout'
import { FilePreview, InputFile, InputFileContainer } from 'components/v2/InputFile'
import { Row } from 'components/v2/Row'
import { FILE_CATEGORIES_LIST, paymentSummaryCategoryMessages } from 'features/PaymentsSummary/constants'
import { IPaymentSummariesFormValues, IPaymentSummaryFile } from 'features/PaymentsSummary/types'
import { usePushFileToS3 } from 'utils/file'
import { CreatePartnersPaymentSummaryCategoryInput } from 'gql/generated/graphql'
import { schema } from './schema'
import { PaymentSummaryFilesStatus } from 'features/PaymentsSummary/components/NewPaymentSummaries/PaymentSummaryFilesStatus'

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr minmax(200px, min-content);
  gap: 20px;
`

const FilePreviewWrapper = styled.div`
  width: 50%;
  min-width: 200px;
`

const messages = defineMessages({
  namePlaceholder: {
    id: 'paymentSummary.new.form.name.placeholder',
    defaultMessage: 'Enter a name'
  }
})

interface IPaymentSummariesFormCardProps {
  setIsUploadingFile: (isLoading: boolean) => void
  isEdit?: boolean
}

const PaymentSummariesFormCard = ({
  setIsUploadingFile,
  isEdit = false
}: IPaymentSummariesFormCardProps): ReactElement => {
  const intl = useIntl()
  const pushFileToS3 = usePushFileToS3()
  const [uploadingFiles, setUploadingFiles] = useState<CreatePartnersPaymentSummaryCategoryInput[]>([])
  const { values, setValues, handleChange, handleBlur, touched, errors } =
    useFormikContext<IPaymentSummariesFormValues>()

  const handleFileChange = async (
    newFile: File | null | '',
    fileCategory: CreatePartnersPaymentSummaryCategoryInput
  ): Promise<void> => {
    setValues({
      ...values,
      files: values.files.map((file) => {
        if (file.category === fileCategory) {
          return {
            category: fileCategory,
            file: '',
            path: ''
          }
        }
        return file
      })
    })
    if (newFile) {
      try {
        setIsUploadingFile(true)
        setUploadingFiles([...uploadingFiles, fileCategory])
        const path = await pushFileToS3(newFile as File)
        setValues({
          ...values,
          files: values.files.map((file) => {
            if (file.category === fileCategory) {
              return {
                category: fileCategory,
                file: newFile || '',
                path
              }
            }
            return file
          })
        })
      } catch (error) {
        return
      } finally {
        setIsUploadingFile(false)
        setUploadingFiles(uploadingFiles.filter((uploadingFileCategory) => uploadingFileCategory !== fileCategory))
      }
    }
  }

  return (
    <FormLayout.Card small>
      <FormGrid>
        <div>
          <Row>
            <Column>
              <Input
                id="name"
                label={<FormattedMessage id="paymentSummary.new.form.name.label" defaultMessage="Name" />}
                placeholder={intl.formatMessage(messages.namePlaceholder)}
                value={values.name}
                onChange={handleChange}
                onBlur={handleBlur}
                touched={touched.name}
                errors={errors.name}
                disabled={isEdit}
              />
            </Column>
          </Row>
          {FILE_CATEGORIES_LIST.map((fileCategory) => {
            const file = values.files.find((file) => file.category === fileCategory) as IPaymentSummaryFile
            let acceptedFiles: Record<string, string[]> = { 'text/csv': [] }
            if (fileCategory === CreatePartnersPaymentSummaryCategoryInput.Calendar) {
              acceptedFiles = { 'image/jpeg': [], 'image/png': [], 'image/jpg': [] }
            }

            return (
              <Row key={fileCategory}>
                <Column>
                  <InputFileContainer>
                    <InputFile
                      label={intl.formatMessage(paymentSummaryCategoryMessages[fileCategory])}
                      onChange={async (file): Promise<void> => {
                        handleFileChange(file, fileCategory)
                      }}
                      accept={acceptedFiles}
                    />
                    {uploadingFiles.includes(fileCategory) && (
                      <FormattedMessage
                        id="paymentSummary.new.form.documents.uploading"
                        defaultMessage="Uploading..."
                      />
                    )}
                    {file.file ? (
                      <FilePreviewWrapper>
                        <FilePreview
                          file={file.file || ''}
                          filename={(file.file as File)?.name || ''}
                          onRemoveFile={(): void => {
                            handleFileChange(null, fileCategory)
                          }}
                        />
                      </FilePreviewWrapper>
                    ) : null}
                  </InputFileContainer>
                </Column>
              </Row>
            )
          })}
        </div>
        <div>
          <PaymentSummaryFilesStatus />
        </div>
      </FormGrid>
    </FormLayout.Card>
  )
}

interface INewPaymentSummariesProps {
  initialValues: IPaymentSummariesFormValues
  onSubmit: (values: IPaymentSummariesFormValues, helpers: FormikHelpers<IPaymentSummariesFormValues>) => void
  loading?: boolean
  error?: ApolloError
  isEdit?: boolean
}

export const NewPaymentSummaries = ({
  initialValues,
  onSubmit,
  loading,
  error,
  isEdit = false
}: INewPaymentSummariesProps): ReactElement => {
  const intl = useIntl()
  const [isUploadingFile, setIsUploadingFile] = useState(false)

  return (
    <Formik initialValues={initialValues} validationSchema={schema(intl)} onSubmit={onSubmit}>
      {({ values, handleSubmit, dirty }): ReactElement => {
        const hasAddedFiles = values.files.some((file) => !!file.file)
        const isSubmitDisabled = !dirty || !hasAddedFiles || isUploadingFile

        return (
          <FormLayout.Content as="form" onSubmit={handleSubmit}>
            <PaymentSummariesFormCard setIsUploadingFile={setIsUploadingFile} isEdit={isEdit} />
            {error && (
              <FormLayout.Actions>
                <ErrorMessage errors={getUnprocessableErrors(error)}>
                  <FormattedMessage
                    id="paymentSummary.new.error"
                    defaultMessage="There was an error while saving the payment summary."
                  />
                </ErrorMessage>
              </FormLayout.Actions>
            )}
            <FormLayout.Actions>
              <Button kind="cancel" as={NavLink} to="/partners/payment-summary">
                <FormattedMessage id="dictionary.cancel" defaultMessage="Cancel" />
              </Button>
              <Button type="submit" kind="primary" disabled={isSubmitDisabled} loading={loading}>
                {isEdit ? (
                  <FormattedMessage id="dictionary.update" defaultMessage="Update" />
                ) : (
                  <FormattedMessage id="dictionary.save" defaultMessage="Save" />
                )}
              </Button>
            </FormLayout.Actions>
          </FormLayout.Content>
        )
      }}
    </Formik>
  )
}
